/**
 * lib/kartoteki/kartoteka-definition.ts — hook pobierający pełną definicję kartoteki
 * systemowej (pola + layouty) przez endpoint M1.2:
 *   GET /api/admin/kartoteki/{slug}/definition
 *
 * Używany przez kreator formularza encji-odwołania (M3b) do zasilenia adaptera buildera
 * polami kartoteki (read-only — add-only policy Etapu 2).
 */

import type { KartotekaViewDto } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

// ─── Typy odpowiedzi endpointu definicji kartoteki ───────────────────────────

/** Pojedyncze pole kartoteki systemowej (rdzenowe i dodane). */
export interface KartotekaBuilderField {
  id: string;
  slug: string;
  name: string;
  fieldType: string;
  isCore: boolean;
  isSystemLocked: boolean;
  isRequired: boolean;
  sortOrder: number;
  /** Kto dodał pole (imię/nazwisko admina ITE) — tylko dla pól dodanych (isCore=false). */
  addedByName?: string | null;
  /** Kiedy dodano (ISO) — tylko dla pól dodanych. */
  addedAt?: string | null;
  /**
   * Kiedy pole zostało po raz pierwszy opublikowane (wysłane do tenantów) — ISO string.
   * `null` = pole nigdy nie opublikowane → można je usunąć (soft-delete).
   * Tylko dla pól dodanych (isCore=false); core pola zawsze nieokreślone.
   */
  firstPublishedAt?: string | null;
  /**
   * Tylko w widoku TYPU dokumentu (definicja pobrana z `?typeId=`): `true` = pole
   * odziedziczone z klasy (rdzeń lub pole wspólne) — tylko do wglądu; `false` = własne
   * pole tego typu (edytowalne). Niezdefiniowane w widoku klasy.
   */
  inherited?: boolean;
}

/** Layout zapisany lub syntetyczny domyślny (buildDefaultLayoutConfig). */
export interface KartotekaBuilderLayout {
  id: string;
  moduleSlug: string;
  layoutType: 'form' | 'list' | string;
  name: string;
  isDefault: boolean;
  layoutConfig: unknown;
  listConfig?: unknown;
  categoryId: string | null;
  roleIds: string[];
}

/**
 * Domyślny widok listy kartoteki (Etap 2 B) — generowany przez
 * buildDefaultListConfig (slug-driven z pól showInList).
 * Front używa jako seed gdy brak zapisanego per-moduł listConfig.
 */
export interface KartotekaDefaultListConfig {
  columns: { id: string; visible: boolean; width: number | string; order: number }[];
  defaultSort: null;
  defaultFilters: [];
  formattingRules: [];
  quickFilters: [];
  defaultViewMode: 'list';
  widgets: [];
}

/** Odpowiedź GET /api/admin/kartoteki/{slug}/definition */
export interface KartotekaDefinition {
  module: {
    slug: string;
    name: string;
    entityTableName: string;
    icon: string;
    color: string;
    isHelperEntity: boolean;
  };
  fields: KartotekaBuilderField[];
  layouts: KartotekaBuilderLayout[];
  categories: unknown[];
  /** Etap 2 B — domyślny widok listy (slug-driven, showInList pola). */
  defaultListConfig?: KartotekaDefaultListConfig;
  /**
   * Etap 2 M1.3 fix — pola helper-encji (podkolekcji) per slug kolekcji.
   * Zasilane przez embedded builder formularza (SubcollectionLayoutEditor) bez
   * tenant-scope fetch. Klucze to slugi kolekcji (np. 'contractor_contacts'),
   * wartości to tablice pól (format identyczny z KartotekaBuilderField).
   */
  helperFields?: Record<string, KartotekaBuilderField[]>;
  /**
   * Domyślne layouty (sekcje + pola) helper-encji per slug kolekcji.
   * Budowane przez buildDefaultLayoutConfig analogicznie do głównego modułu.
   * SubcollectionLayoutEditor (embedded=true) używa jako fallback gdy
   * formLayout === undefined — seed-effect inicjalizuje canvas sekcjami z tych layoutów.
   */
  helperDefaultLayouts?: Record<string, unknown>;
}

// ─── API client ──────────────────────────────────────────────────────────────

/**
 * Usuwa (soft-delete) pole kartoteki systemowej.
 * Dozwolone tylko gdy pole NIE jest rdzeniowe (isCore=false) i NIE było jeszcze
 * opublikowane (firstPublishedAt === null).
 * Backend: DELETE /api/admin/kartoteki/:slug/fields/:fieldId
 */
export async function deleteKartotekaField(slug: string, fieldId: string): Promise<void> {
  await api.delete(`/admin/kartoteki/${slug}/fields/${fieldId}`);
}

// ─── Fleet push (M4/T3.4) — wyślij pola + layout + nazwane widoki do tenantów ─

/** Wynik `apply` dla pojedynczego tenanta (fields+layout etap 1, widoki etap 2). */
export interface KartotekaApplyTenantOutcome {
  tenantId: string;
  code: string;
  fieldsAdded?: string[];
  fieldsSkipped?: string[];
  layoutsApplied?: ('form' | 'list')[];
  viewsCreated?: number;
  viewsUpdated?: number;
  viewsRemoved?: number;
  viewWarnings?: number;
  viewsInvalid?: number;
  /** Etap 1 (apply) się powiódł, ale materializacja widoków/audyt B1 rzuciły — wynik częściowy. */
  viewsError?: string;
  /** Etap 1 (apply) sam rzucił — pełna porażka tego tenanta. */
  error?: string;
}

/** Odpowiedź POST /api/admin/kartoteki/{slug}/apply. */
export interface KartotekaApplyResult {
  slug: string;
  dryRun: boolean;
  totals: {
    tenants: number;
    succeeded: number;
    failed: number;
    fieldsAdded: number;
    viewsCreated: number;
    viewsUpdated: number;
    viewsRemoved: number;
    viewsInvalid: number;
    /** Tenanty gdzie apply się powiódł, ale materializacja widoków rzuciła (wynik częściowy). */
    viewsFailed: number;
  };
  tenants: KartotekaApplyTenantOutcome[];
}

/**
 * Fleet push kartoteki systemowej — wysyła pola + layout domyślny + nazwane widoki
 * formularza (biblioteka `kartoteka_views`) do WSZYSTKICH aktywnych tenantów.
 * Add-only, idempotentnie. Bez `dryRun` — realny zapis.
 * Backend: POST /api/admin/kartoteki/{slug}/apply
 */
export async function applyKartotekaToTenants(slug: string): Promise<KartotekaApplyResult> {
  const res = await api.post<KartotekaApplyResult>(`/admin/kartoteki/${slug}/apply`);
  return res.data;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Pobiera pełną definicję kartoteki systemowej (pola + layouty).
 * Zasilany slugiem kartoteki (np. 'contractors', 'employees').
 * `enabled: false` gdy slug pusty — nie strzela bez potrzeby.
 *
 * `typeId` (opcjonalny) → widok TYPU dokumentu: definicja niesie pola wspólne klasy
 * (odziedziczone, `inherited:true`) + własne pola typu (`inherited:false`). Klucz zapytania
 * zawiera typeId, więc widok klasy i widok typu są cache'owane osobno. Prefiks klucza pozostaje
 * `['admin','kartoteki',slug,'definition',…]`, więc dotychczasowe inwalidacje nadal trafiają.
 */
export function useKartotekaDefinition(slug: string | undefined, typeId?: string) {
  return useQuery<KartotekaDefinition>({
    queryKey: ['admin', 'kartoteki', slug, 'definition', typeId ?? null],
    queryFn: async () => {
      const url = typeId
        ? `/admin/kartoteki/${slug}/definition?typeId=${encodeURIComponent(typeId)}`
        : `/admin/kartoteki/${slug}/definition`;
      return (await api.get<KartotekaDefinition>(url)).data;
    },
    enabled: Boolean(slug),
    retry: false,
  });
}

// ─── Bramkowanie pól per typ dokumentu (ADR-0085) ────────────────────────────

/** Odczyt bramkowania typu: pula udostępnionych pól + slugi wykluczonych pól rdzeniowych. */
export interface TypeGatingState {
  /**
   * `true` = typ ma własne ustawienie pól (lista pól dodatkowych poniżej obowiązuje, także pusta);
   * `false` = typ bez ustawienia — u klientów obowiązują wszystkie pola dodatkowe klasy.
   */
  hasFieldSetting: boolean;
  /** Id pól z puli klasy udostępnionych typowi (`system_kartoteka_type_fields`). */
  gatedFieldIds: string[];
  /** Podzbiór `gatedFieldIds` oznaczony jako WYMAGANE w typie (reszta udostępnionych = opcjonalne). */
  requiredFieldIds: string[];
  /** Slugi pól rdzeniowych WYŁĄCZONYCH dla typu (brak = wszystkie rdzeniowe dostępne). */
  excludedCoreFieldSlugs: string[];
}

export function useTypeGatedFields(slug: string | undefined, typeId: string | undefined) {
  return useQuery<TypeGatingState>({
    queryKey: ['admin', 'kartoteki', slug, 'gated-fields', typeId ?? null],
    queryFn: async () =>
      (await api.get<TypeGatingState>(`/admin/kartoteki/${slug}/gated-fields/${typeId}`)).data,
    enabled: Boolean(slug && typeId),
    retry: false,
  });
}

/** Zapis (pełne zastąpienie) bramkowania typu — pula + wymagalność + wykluczenia core; inwaliduje odczyt. */
export function useSaveTypeGating(slug: string, typeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      hasFieldSetting: boolean;
      fieldIds: string[];
      requiredFieldIds?: string[];
      excludedCoreFieldSlugs?: string[];
    }) =>
      (await api.put<TypeGatingState>(`/admin/kartoteki/${slug}/gated-fields/${typeId}`, input))
        .data,
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['admin', 'kartoteki', slug, 'gated-fields', typeId],
      });
    },
  });
}

// ─── Tożsamość kartoteki (ADR-0060) — wspólny adres biblioteki widoków ────────

/**
 * Adres biblioteki widoków kartoteki (`kartoteka_views`, ADR-0060):
 *  - kartoteka SYSTEMOWA — slug modułu systemowego (np. 'contractors'),
 *  - kartoteka KREATORA (encja `new_table` szablonu, Faza 2) — UUID encji
 *    (`module_template_entities.id`; slug encji jest edytowalny, więc niestabilny).
 * Lustro backendowego `resolveKartotekaIdentity` (apps/api/lib/kartoteka-views/identity.ts).
 */
export type KartotekaIdentityRef =
  | { kind: 'system'; key: string }
  | { kind: 'creator'; key: string };

/** Segment trasy pokrywa się z kind: /admin/kartoteki/{system|creator}/{key}/views. */
function kartotekaViewsBasePath(identity: KartotekaIdentityRef): string {
  return `/admin/kartoteki/${identity.kind}/${identity.key}/views`;
}

// ─── Library views (GET /api/admin/kartoteki/{system|creator}/[key]/views) ────

/** Odpowiedź GET .../views */
interface KartotekaViewsResponse {
  views: KartotekaViewDto[];
}

/**
 * React Query key wspólny dla odczytu i wszystkich mutacji widoków biblioteki
 * (invalidacja). Gałąź system zachowuje HISTORYCZNY kształt klucza
 * (['admin','kartoteki',slug,'system-views']) — inwalidacje muszą trafiać w
 * istniejące subskrypcje (`useKartotekaSystemViews` w AdminGlobalDocumentTypes).
 */
export function kartotekaLibraryViewsQueryKey(identity: KartotekaIdentityRef) {
  return identity.kind === 'system'
    ? (['admin', 'kartoteki', identity.key, 'system-views'] as const)
    : (['admin', 'kartoteki', 'creator', identity.key, 'views'] as const);
}

/** Widoki biblioteki kartoteki (zapytanie bez hooka — np. dla wielu kartotek naraz). */
export async function fetchKartotekaLibraryViews(
  identity: KartotekaIdentityRef,
): Promise<KartotekaViewDto[]> {
  return (await api.get<KartotekaViewsResponse>(kartotekaViewsBasePath(identity))).data.views;
}

/**
 * Pobiera widoki biblioteki kartoteki (formularze + listy) — systemowej lub Kreatora.
 * `enabled: false` gdy identity nieustalone (np. encja szablonu jeszcze bez UUID —
 * struktura niezapisana).
 */
export function useKartotekaLibraryViews(identity: KartotekaIdentityRef | undefined) {
  return useQuery<KartotekaViewDto[]>({
    queryKey: identity
      ? kartotekaLibraryViewsQueryKey(identity)
      : ['admin', 'kartoteki', 'library-views', 'disabled'],
    queryFn: async () =>
      (await api.get<KartotekaViewsResponse>(kartotekaViewsBasePath(identity!))).data.views,
    enabled: Boolean(identity),
    retry: false,
  });
}

/**
 * Pobiera widoki biblioteki kartoteki systemowej — wrapper zachowany dla istniejących
 * callerów (AdminGlobalDocumentTypes); nowy kod używa `useKartotekaLibraryViews`.
 */
export function useKartotekaSystemViews(slug: string | undefined) {
  return useKartotekaLibraryViews(slug ? { kind: 'system', key: slug } : undefined);
}

/** Wrapper klucza dla gałęzi systemowej — patrz `kartotekaLibraryViewsQueryKey`. */
export function kartotekaSystemViewsQueryKey(slug: string) {
  return kartotekaLibraryViewsQueryKey({ kind: 'system', key: slug });
}

// ─── Mutacje widoków biblioteki (CRUD, Etap 3 T3.1 — autoring w panelu ITE) ───

/** Wejście do utworzenia/aktualizacji widoku biblioteki kartoteki. */
export interface KartotekaViewUpsertInput {
  slug: string;
  name: string;
  viewType: 'form' | 'list';
  config: Record<string, unknown>;
  sortOrder?: number;
}

/** Alias historyczny (Etap 3 T3.1 — przed Fazą 2 API było system-only). */
export type KartotekaSystemViewUpsertInput = KartotekaViewUpsertInput;

/**
 * Tworzy nowy widok biblioteki. UWAGA: backend robi UPSERT po (kind, key, slug) —
 * POST na slug, który już istnieje, po cichu NADPISZE istniejący widok. Caller musi
 * sprawdzić unikalność slugu kliencko (patrz `CreateKartotekaViewModal`).
 * POST /api/admin/kartoteki/{system|creator}/{key}/views
 */
export async function createKartotekaLibraryView(
  identity: KartotekaIdentityRef,
  input: KartotekaViewUpsertInput,
): Promise<KartotekaViewDto> {
  const res = await api.post<{ view: KartotekaViewDto }>(kartotekaViewsBasePath(identity), input);
  return res.data.view;
}

/**
 * Aktualizuje istniejący widok (slug widoku pochodzi z trasy — niezmienny przez PATCH).
 * Upsert po stronie backendu wymaga PEŁNEGO configu — caller musi przesłać niezmienione
 * pola (np. przy samej zmianie nazwy: ten sam config/viewType/sortOrder).
 * PATCH /api/admin/kartoteki/{system|creator}/{key}/views/{viewSlug}
 */
export async function updateKartotekaLibraryView(
  identity: KartotekaIdentityRef,
  viewSlug: string,
  input: Omit<KartotekaViewUpsertInput, 'slug'>,
): Promise<KartotekaViewDto> {
  const res = await api.patch<{ view: KartotekaViewDto }>(
    `${kartotekaViewsBasePath(identity)}/${viewSlug}`,
    input,
  );
  return res.data.view;
}

/**
 * Soft-delete widoku. Backend odmawia (409 KARTOTEKA_VIEW_PUBLISHED_CANNOT_DELETE) gdy
 * widok był kiedykolwiek opublikowany — UI powinno zablokować przycisk zanim dojdzie
 * do wywołania (guard kliencki lustrzany do pól kartoteki), ale wywołanie zostaje
 * bezpieczne również przy race condition.
 * DELETE /api/admin/kartoteki/{system|creator}/{key}/views/{viewSlug}
 */
export async function deleteKartotekaLibraryView(
  identity: KartotekaIdentityRef,
  viewSlug: string,
  reason?: string,
): Promise<void> {
  await api.delete(`${kartotekaViewsBasePath(identity)}/${viewSlug}`, {
    data: reason ? { reason } : undefined,
  });
}
